import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { Plus, PackageCheck, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';

interface Catalog {
  id: string;
  name: string;
  description?: string;
  status: string;
  itemsCount: number;
  createdAt: string;
  updatedAt: string;
}

export function VendorPortalCatalogsPage() {
  const { sessionId } = useAuth();
  const [, setLocation] = useLocation();
  
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Fetch catalogs
  const { data: catalogs, isLoading } = useQuery({
    queryKey: ['vendor-portal-catalogs', sessionId],
    queryFn: async () => {
      const result = await api.get<{ catalogs: Catalog[] }>('/vendor-portal/catalogs', sessionId);
      return result.catalogs;
    },
    enabled: !!sessionId,
  });

  // Filter catalogs
  const filteredCatalogs = (catalogs || []).filter(catalog => {
    if (searchValue) {
      const q = searchValue.toLowerCase();
      const matches =
        catalog.name.toLowerCase().includes(q) ||
        catalog.description?.toLowerCase().includes(q);
      if (!matches) return false;
    }
    if (statusFilter !== 'all' && catalog.status !== statusFilter) return false;
    return true;
  });

  const handleRowClick = (catalogId: string) => {
    setLocation(`/vendor-portal/catalogs/${catalogId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Catalog Upload</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage vendor product catalogs
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setLocation('/vendor-portal/catalogs/new')}>
            <Plus className="h-4 w-4 mr-2" />
            New Catalog
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search catalogs..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="text-center py-12 text-gray-500">
              Loading catalogs...
            </div>
          ) : filteredCatalogs.length === 0 ? (
            <div className="text-center py-12">
              <PackageCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No catalogs found</p>
              <p className="text-sm text-gray-400 mt-2">
                Upload your first catalog to get started
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Catalog Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Items
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Updated
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCatalogs.map((catalog) => (
                    <tr
                      key={catalog.id}
                      onClick={() => handleRowClick(catalog.id)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">
                          {catalog.name}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-500 line-clamp-2">
                          {catalog.description || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={
                          catalog.status === 'active' ? 'default' :
                          catalog.status === 'draft' ? 'secondary' :
                          'outline'
                        }>
                          {catalog.status}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {catalog.itemsCount} items
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(catalog.updatedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
